import React, { useState, useEffect } from 'react';
import { useDebounce } from 'usehooks-ts';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import CheckBoxIcon from '@mui/icons-material/CheckBox';

import { ContainedButton, OutlinedButton } from '../../modules/Buttons';
import styles from './FIlterCollaboratorModal.module.scss';
import { USER } from '../../../enums';
import { getMyFolderCollaborators } from '../../../api';
import SearchInput from '../../modules/SearchInput';
import Modal from '../../modules/Modal';

type FilterFolderModalProps = {
  open: boolean;
  setOpen: (b: boolean) => void;
  onApply: (userIds: string[]) => void;
  currentUserId: string;
};

const FilterCollaboratorModal: React.FC<FilterFolderModalProps> = ({ open, setOpen, onApply, currentUserId }) => {
  const [search, setSearch] = useState('');
  const [ignore, setIgnore] = useState<string[]>([currentUserId]);
  const [collaborators, setCollaborators] = useState<USER[]>([]);
  const [selected, setSelected] = useState<USER[]>([]);
  const [loading, setLoading] = useState(false);

  const fetch = async () => {
    try {
      setLoading(true);
      const res = await getMyFolderCollaborators();
      if (res.data) {
        setCollaborators(res.data);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) {
      fetch();
    }
  }, [open]);

  const onSelect = (u: USER) => {
    setSelected((prev) => [...prev, u]);
    setIgnore((prev) => [...prev, u.id]);
    setSearch('');
  };

  const onRemove = (id: string) => {
    setSelected((prev) => prev.filter((u) => u.id !== id));
    setIgnore((prev) => prev.filter((x) => x !== id));
  };

  const handleApply = () => {
    onApply(selected.map((u) => u.id));
    setOpen(false);
  };

  const onClose = () => {
    onApply(selected.map((u) => u.id));
    setOpen(false);
  };

  return (
    <Modal open={open} onClose={onClose} loading={loading}>
      <div className={styles.modalContainer}>
        <div className={styles.modalTitle}>Filter by collaborator</div>
        <SearchInput placeHolder="Search users" onChange={(e) => setSearch(e.target.value)} className={styles.search} value={search} />
        {collaborators.length > 0 && (
          <div className={styles.results}>
            {collaborators
              .filter((us) => !ignore.includes(us.id))
              .filter(
                (us) =>
                  us?.username?.toLocaleLowerCase()?.includes(search?.toLocaleLowerCase()) ||
                  us?.email?.toLocaleLowerCase()?.includes(search?.toLocaleLowerCase()),
              )
              .map((u) => (
                <div className={styles.collUser} key={u.id} onClick={() => onSelect(u)}>
                  <div className={styles.collInfo}>{u.username}</div> <CheckBoxOutlineBlankIcon />
                </div>
              ))}
          </div>
        )}
        {selected.length > 0 && (
          <>
            <div className={styles.label}>Selected collaborators</div>
            <div className={styles.selected}>
              {selected.map((u) => (
                <div key={u.id} className={styles.badge} onClick={() => onRemove(u.id)}>
                  <span>{u.username}</span>

                  <CheckBoxIcon className={styles.buttonRemove} />
                </div>
              ))}
            </div>
          </>
        )}
        <div className={styles.actions}>
          <OutlinedButton disabled={loading} onClick={onClose}>
            Cancel
          </OutlinedButton>
          <ContainedButton onClick={handleApply} disabled={loading}>
            Apply
          </ContainedButton>
        </div>
      </div>
    </Modal>
  );
};

export default FilterCollaboratorModal;
